import { RestApiServiceParamsContext, RestApiServiceParams, restApiService } from '@tableau/vizportalclient-common-components';
import { defaultContext, QueryClient, useQuery } from '@tanstack/react-query';
import React from 'react';
import { Formatters } from '../../../../../../gen-src/Localize';
import {
  DatabaseLineageLabelsQuery,
  DatabaseLineageLabelsDocument,
  EmbeddedDatasourceLineageLabelsQuery,
  EmbeddedDatasourceLineageLabelsDocument,
  ViewLineageLabelsQuery,
  ViewLineageLabelsDocument,
  MetricLineageLabelsQuery,
  MetricLineageLabelsDocument,
  ColumnLineageLabelsDocument,
  FieldLineageLabelsDocument,
  DatabaseTableLineageLabelsDocument,
  VirtualConnectionTablesLineageLabelsDocument,
  FlowLineageLabelsDocument,
  PublishedDatasourceLineageLabelsDocument,
  VirtualConnectionLineageLabelsDocument,
  WorkbookLineageLabelsDocument,
  FlowColumnFieldLineageLabelsDocument,
  FlowFieldLineageLabelsDocument
} from '../../../../../../gen-src/queries';
import { TableauUser } from '../../UserDisplayComponent';
import { FeatureFlagsContext } from '../../featureFlags/FeatureFlagsContext';
import { useLockInLoadedValue } from '../../hooks/useLockInLoadedValue';
import { useTelemetrySender } from '../../hooks/useTelemetrySender';
import { ReactTelemetryContext, TelemetryContext } from '../../telemetry/TelemetryContext';
import { ValidTelemetryEvent } from '../../telemetry/TelemetrySender';
import { assertUnreachable } from '../../utils/TypescriptHelpers';
import { LabelButton } from '../LabelButtons';
import { LabelsTelemetryEvent } from '../LabelsTelemetryEvent';
import { HydratedCertificationRenderProps } from '../certification/CertificationProps';
import { RestLabelResponse, isLabelContentType } from '../restApi/LabelTypes';
import { getLabels } from '../restApi/Labels';
import { HydratedSensitivityRenderProps } from '../sensitivity/SensitivityProps';
import { LabelContentItem, RenderLabel, LabelRenderControls } from '../shared/LabelRenderProps';
import { HydratedWarningRenderProps, RenderAnchoredWarning } from '../warning/WarningProps';
import { ContextOverrides, useContextWithOverride } from './HookContext';
import { useGetUsers } from './getUsers'
import { useColumnLineageLabelsLazyQuery } from './lazyQueries/useColumnLineageLabelsLazyQuery';
import { useDatabaseTableLineageLabelsLazyQueryInheritedLabels } from './lazyQueries/useDatabaseTableLineageLabelsLazyQuery';
import { useFieldLineageLabelsLazyQuery } from './lazyQueries/useFieldLineageLabelsLazyQuery';
import { useFlowColumnFieldLineageLabelsLazyQuery } from './lazyQueries/useFlowColumnFieldLineageLabelsLazyQuery';
import { useFlowFieldLineageLabelsLazyQuery } from './lazyQueries/useFlowFieldLineageLabelsLazyQuery';
import { useFlowLineageLabelsLazyQuery } from './lazyQueries/useFlowLineageLabelsLazyQuery';
import { useGeneralLineageLabelsLazyQuery } from './lazyQueries/useGeneralLineageLabelsLazyQuery';
import { usePublishedDatasourceLineageLabelsLazyQuery } from './lazyQueries/usePublishedDatasourceLineageLabelsLazyQuery';
import { useVirtualConnectionLineageLabelsLazyQuery } from './lazyQueries/useVirtualConnectionLineageLabelsLazyQuery';
import { useVirtualConnectionTableLineageLabelsLazyQuery } from './lazyQueries/useVirtualConnectionTableLineageLabelsLazyQuery';
import { useWorkbookLineageLabelsLazyQuery } from './lazyQueries/useWorkbookLineageLabelsLazyQuery';
import { UseLabelsResultWithExecuteFunction, UseLabelsResult } from './useRenderLabelsTypes';

export type ProbablyRenderLabels = {
  readonly certified?: boolean;
  readonly warning?: { readonly value: string; readonly severe: boolean; readonly count: number; }
  readonly sensitivity?: {readonly value: string; readonly count: number; };
}

export type UseRenderLabelsParams = {
  readonly catalogEnabled: boolean;
  readonly content: LabelContentItem;
  readonly includeUpstream?: boolean;
  readonly includeParented?: boolean;
  readonly probablyOrEager?: 'eager' | ProbablyRenderLabels;
  readonly contextOverrides?: ContextOverrides;
} & {
  readonly testId: string;
  readonly source?: string; // TODO this should be required in the future for telemetry
  readonly tabIndex?: number;
  readonly controls?: LabelRenderControls;
};

export type UseRenderLabelsResult = {
  readonly warnings: JSX.Element | null;
  readonly certifications: JSX.Element | null;
  readonly sensitivities: JSX.Element | null;
}

type TransformedRestLabels = {
  warnings: RenderAnchoredWarning[];
  certifications: RenderLabel[];
  sensitivities: RenderLabel[];
}

async function getRestLabels(items: LabelContentItem, params: RestApiServiceParams): Promise<ReadonlyArray<RestLabelResponse>> {
  if (!items.contentType || !isLabelContentType(items.contentType) || !items.contentLuid) {
    return [];
  }
  const { makeJsonRestApiRequest } = restApiService(params);
  const result = await getLabels({
    contentItems: [{
      contentId: items.contentLuid,
      contentType: items.contentType,
      contentName: items.contentName,
    }],
    makeJsonRestApiRequest,
    transform: (t) => t,
  });
  const labels = result.get({ contentId: items.contentLuid, contentType: items.contentType.toLowerCase() });
  return labels ?? [];
}

function useLabelsGraph(item: LabelContentItem, controls?: LabelRenderControls, includeUpstream?: boolean, includeParented?: boolean, contextOverrides?: ContextOverrides): UseLabelsResultWithExecuteFunction {
  const column = useColumnLineageLabelsLazyQuery({ item, controls, includeUpstream, includeParented, contextOverrides, document: ColumnLineageLabelsDocument });
  const database = useGeneralLineageLabelsLazyQuery<DatabaseLineageLabelsQuery>({ item, controls, includeUpstream, includeParented, contextOverrides, document: DatabaseLineageLabelsDocument });
  const databaseTable = useDatabaseTableLineageLabelsLazyQueryInheritedLabels({ item, controls, includeUpstream, includeParented, contextOverrides, document: DatabaseTableLineageLabelsDocument });
  const virtualConnectionTable = useVirtualConnectionTableLineageLabelsLazyQuery({ item, controls, includeUpstream, includeParented, contextOverrides, document: VirtualConnectionTablesLineageLabelsDocument });
  const flow = useFlowLineageLabelsLazyQuery({ item, controls, includeUpstream, includeParented, contextOverrides, document: FlowLineageLabelsDocument });
  const publishedDatasource = usePublishedDatasourceLineageLabelsLazyQuery({ item, controls, includeUpstream, includeParented, contextOverrides, document: PublishedDatasourceLineageLabelsDocument });
  const embeddedDatasource = useGeneralLineageLabelsLazyQuery<EmbeddedDatasourceLineageLabelsQuery>({ item, controls, includeUpstream, includeParented, contextOverrides, document: EmbeddedDatasourceLineageLabelsDocument });
  const virtualConnection = useVirtualConnectionLineageLabelsLazyQuery({ item, controls, includeUpstream, includeParented, contextOverrides, document: VirtualConnectionLineageLabelsDocument });
  const view = useGeneralLineageLabelsLazyQuery<ViewLineageLabelsQuery>({ item, controls, includeUpstream, includeParented, contextOverrides, document: ViewLineageLabelsDocument });
  const workbook = useWorkbookLineageLabelsLazyQuery({ item, controls, includeUpstream, includeParented, contextOverrides, document: WorkbookLineageLabelsDocument });
  const field = useFieldLineageLabelsLazyQuery({ item, controls, includeUpstream, includeParented, contextOverrides, document: FieldLineageLabelsDocument });
  const metric = useGeneralLineageLabelsLazyQuery<MetricLineageLabelsQuery>({ item, controls, includeParented, includeUpstream, contextOverrides, document: MetricLineageLabelsDocument });
  const flowField = useFlowFieldLineageLabelsLazyQuery({ item, controls, includeUpstream, includeParented, contextOverrides, document: FlowFieldLineageLabelsDocument });
  const flowColumnField = useFlowColumnFieldLineageLabelsLazyQuery({ item, controls, includeUpstream, includeParented, contextOverrides, document: FlowColumnFieldLineageLabelsDocument });

  switch (item.subjectArea) {
    case 'column': case 'Column':
      return column;
    case 'database': case 'CloudFile': case 'DatabaseServer': case 'File': case 'WebDataConnector':
      return database;
    case 'table': case 'DatabaseTable':
      return databaseTable;
    case 'virtualConnectionTable': case 'VirtualConnectionTable':
      return virtualConnectionTable;
    case 'flow': case 'Flow':
      return flow;
    case 'datasource': case 'PublishedDatasource':
      return publishedDatasource;
    case 'EmbeddedDatasource':
      return embeddedDatasource;
    case 'virtualConnection': case 'VirtualConnection':
      return virtualConnection;
    case 'View':
      return view;
    case 'Workbook':
    case 'workbook':
      return workbook;
    case 'Field':
      return field;
    case 'FlowColumnOutputField':
      return flowColumnField;
    case 'FlowFieldOutputField':
      return flowField;
    case 'Metric':
    case 'metric':
      return metric;
    default: // Should never hit this
      assertUnreachable(item.subjectArea ?? 'undefined')
      return [() => { return }, {}];
  }
}

function mapRestLabelsToRenderProps(labels?: readonly RestLabelResponse[], authors?: Map<string, TableauUser>): TransformedRestLabels {
  const activeLabels = labels ? labels.filter((p) => p.active) : [];
  return ({
    warnings: activeLabels.filter((p) => p.category.toLowerCase() === 'warning').map(
      warning => {
        return {
          severe: warning.elevated,
          type: warning.value,
          active: warning.active,
          author: authors?.get(warning.owner?.id ?? '') ?? { username: '', name: warning.userDisplayName, domain: '' },
          message: warning.message,
          updatedAt: Formatters.formatMediumDateTimeWithoutSeconds(new Date(warning.updatedAt)),
        }
      }
    ),
    certifications: activeLabels.filter((p) => p.category.toLowerCase() === 'certification')
      .map(
        c => {
          return {
            type: c.value,
            active: c.active,
            author: authors?.get(c.owner?.id ?? '') ?? { username: '', name: c.userDisplayName, domain: '' },
            message: c.message,
            updatedAt: Formatters.formatMediumDateTimeWithoutSeconds(new Date(c.updatedAt)),
          }
        }
      ),
    sensitivities: activeLabels.filter((p) => p.category.toLowerCase() === 'sensitivity')
      .map(
        s => {
          return {
            type: s.value,
            active: s.active,
            author: authors?.get(s.owner?.id ?? '') ?? { username: '', name: s.userDisplayName, domain: '' },
            message: s.message,
            updatedAt: Formatters.formatMediumDateTimeWithoutSeconds(new Date(s.updatedAt)),
          }
        }
      ),
  });
}

function useLabelsRest(item: LabelContentItem, catalogEnabled: boolean, contextOverrides?: ContextOverrides): UseLabelsResultWithExecuteFunction {
  const restApiService = useContextWithOverride(RestApiServiceParamsContext, contextOverrides?.restApiServiceParams);
  const queryClientContext = React.createContext<QueryClient | undefined>(useContextWithOverride(defaultContext, contextOverrides?.queryClient));
  const [enabled, setEnabled] = React.useState(false);

  const invoke = React.useCallback(() => setEnabled(true), []);
  const { data: _labels, isLoading: _labelsLoading } = useQuery(['labels', item], () => getRestLabels(item, restApiService), { enabled, context: queryClientContext });
  const users: string[] = _labels?.filter(l => l.owner !== undefined).map(l => l.owner?.id) ?? [];
  const { authors, loading: _usersLoading } = useGetUsers(users);

  const { warnings, certifications, sensitivities } = mapRestLabelsToRenderProps(_labels, authors);
  const warningProps: HydratedWarningRenderProps | undefined = warnings.length > 0 && catalogEnabled ? {
    category: 'warning',
    content: item,
    kind: 'hydrated',
    anchored: {
      category: 'warning',
      labels: warnings
    }
  } : undefined;

  const certificationProps: HydratedCertificationRenderProps | undefined = certifications.length > 0 ? {
    category: 'certification',
    content: item,
    kind: 'hydrated',
    anchored: {
      category: 'certification',
      labels: certifications
    }
  } : undefined;

  const sensitivityProps: HydratedSensitivityRenderProps | undefined = sensitivities.length > 0 && catalogEnabled ? {
    category: 'sensitivity',
    content: item,
    kind: 'hydrated',
    anchored: {
      category: 'sensitivity',
      labels: sensitivities
    }
  } : undefined;

  return [invoke, { loading: _usersLoading || _labelsLoading, warnings: warningProps, certifications: certificationProps, sensitivities: sensitivityProps }];
}

function useInitialCallInvoke(invoke: () => void, probablyOrEager?: 'eager' | ProbablyRenderLabels) {
  const [calledInitialInvoke, setCalledInitialInvoke] = React.useState(false);
  if (!calledInitialInvoke && probablyOrEager === 'eager') {
    invoke();
    setCalledInitialInvoke(true);
  }
}

function merge(rest: UseLabelsResult, graph: UseLabelsResult, content: LabelContentItem, customSensitivityLabelsEnabled: boolean): UseLabelsResult {
  let certifications: HydratedCertificationRenderProps | undefined = undefined;
  let warnings: HydratedWarningRenderProps | undefined = undefined;
  let sensitivities: HydratedSensitivityRenderProps | undefined = undefined;
  if (graph.certifications?.parented || rest.certifications?.anchored) {
    certifications = { category: 'certification', content: content, kind: 'hydrated', anchored: rest.certifications?.anchored, parented: graph.certifications?.parented };
  }

  if (graph.warnings?.parented || graph.warnings?.upstream || rest.warnings?.anchored) {
    warnings = { category: 'warning', content: content, kind: 'hydrated', upstream: graph.warnings?.upstream, anchored: rest.warnings?.anchored, parented: graph.warnings?.parented };
  }

  if (customSensitivityLabelsEnabled && (graph.sensitivities?.upstream || graph.sensitivities?.parented || rest.sensitivities?.anchored)) {
    sensitivities = { category: 'sensitivity', content: content, kind: 'hydrated', upstream: graph.sensitivities?.upstream, anchored: rest.sensitivities?.anchored, parented: graph.sensitivities?.parented };
  }

  return {
    loading: (rest.loading || graph.loading) ?? undefined,
    error: rest.error ?? graph.error ?? undefined,
    certifications: certifications,
    warnings: warnings,
    sensitivities: sensitivities
  }
}

function useLabels({ catalogEnabled, content, includeUpstream, includeParented, controls, contextOverrides, probablyOrEager }: UseRenderLabelsParams): UseLabelsResultWithExecuteFunction {
  const { customSensitivityLabelsEnabled } = useContextWithOverride(FeatureFlagsContext, contextOverrides?.featureFlags);
  const [invokeRest, dataRest] = useLabelsRest(content, catalogEnabled, contextOverrides);
  const [invokeGraph, dataGraph] = useLabelsGraph(content, controls, includeUpstream, includeParented, contextOverrides);

  const invoke = React.useCallback(() => {
    invokeRest();
    if (catalogEnabled && (includeUpstream || includeParented)) {
      invokeGraph();
    }
  }, [catalogEnabled, includeUpstream, includeParented, invokeRest, invokeGraph]);

  useInitialCallInvoke(invoke, probablyOrEager);

  return [invoke, merge(dataRest, dataGraph, content, customSensitivityLabelsEnabled)];
}

function isSevere(probably?: ProbablyRenderLabels, data?: HydratedWarningRenderProps): boolean {
  if (probably?.warning) {
    return probably?.warning?.severe;
  }

  let severe = false;

  if (data) {
    data.anchored?.labels.forEach(label => {
      if (label.severe) {
        severe = true;
      }
    });

    data.upstream?.labels.forEach(label => {
      if (label.severe) {
        severe = true;
      }
    });

    data.parented?.labels.forEach(label => {
      if (label.severe) {
        severe = true;
      }
    });
  }

  return severe;
}

function labelValue(defaultType: string, probably?: ProbablyRenderLabels, data?: HydratedWarningRenderProps | HydratedCertificationRenderProps | HydratedSensitivityRenderProps) {
  if (probably?.warning) {
    return probably.warning.value;
  }

  let type: string = defaultType
  data?.anchored?.labels.forEach(label => {
    type = label.type.toString()
  });

  return type;
}

function buildWarnings({ warnings, loading }: UseLabelsResult, { catalogEnabled, content, probablyOrEager, includeUpstream: _includeUpstream, includeParented: includeParented, ...shared }: UseRenderLabelsParams, invoke: () => void, emitTelemetry: (event?: Partial<Omit<ValidTelemetryEvent<LabelsTelemetryEvent>, 'type'>> | undefined) => void): JSX.Element | null {
  if (!catalogEnabled) {
    return null;
  }

  const onClick = () => {
    invoke();
    emitTelemetry({ elevated: isSevere(probably, warnings), value: labelValue('WARNING', probably, warnings) });
  }

  const probably = (probablyOrEager as ProbablyRenderLabels);

  if ((!warnings || loading) && probably?.warning) {
    return <LabelButton category='warning' kind='dehydrated' severe={isSevere(probably, warnings)} content={content} {...shared} loading={loading ?? false} onClick={onClick} count={probably.warning.count} labelValue={probably.warning.value} />
  }

  if (warnings) {
    return <LabelButton {...warnings} {...shared} loading={loading ?? false} onClick={onClick} />
  }

  return null;
}

function buildCertifications({ certifications, loading }: UseLabelsResult, { catalogEnabled, content, probablyOrEager, includeUpstream: _includeUpstream, includeParented: includeParented, ...shared }: UseRenderLabelsParams, invoke: () => void, emitTelemetry: (event?: Partial<Omit<ValidTelemetryEvent<LabelsTelemetryEvent>, 'type'>> | undefined) => void): JSX.Element | null {
  const onClick = () => {
    invoke();
    emitTelemetry({ elevated: false, value: labelValue('CERTIFIED', probably, certifications) });
  }

  const probably = (probablyOrEager as ProbablyRenderLabels);
  if ((!certifications || loading) && probably?.certified) {
    return <LabelButton category='certification' kind='dehydrated' content={content} {...shared} onClick={onClick} loading={loading ?? false} />
  }

  if (certifications) {
    return <LabelButton {...certifications} {...shared} onClick={onClick} loading={loading ?? false} />
  }

  return null;
}

function buildSensitivities({ sensitivities, loading }: UseLabelsResult, { catalogEnabled, content, probablyOrEager, includeUpstream: _includeUpstream, includeParented: includeParented, ...shared }: UseRenderLabelsParams, invoke: () => void, emitTelemetry: (event?: Partial<Omit<ValidTelemetryEvent<LabelsTelemetryEvent>, 'type'>> | undefined) => void): JSX.Element | null {
  if (!catalogEnabled) {
    return null;
  }

  const onClick = () => {
    invoke();
    emitTelemetry({ elevated: false, value: labelValue('SENSITIVE_DATA', probably, sensitivities) });
  }

  const probably = (probablyOrEager as ProbablyRenderLabels);
  if ((!sensitivities || loading) && probably?.sensitivity) {
    return <LabelButton category='sensitivity' kind='dehydrated' content={content} {...shared} onClick={onClick} loading={loading ?? false} count={probably.sensitivity.count} labelValue={probably.sensitivity.value} />
  }

  if (sensitivities) {
    return <LabelButton {...sensitivities} {...shared} onClick={onClick} loading={loading ?? false} />
  }

  return null;
}

function useBuildTelemetryEvents(content_Type: string | null, testId: string, source?: string, telemetryContext?: TelemetryContext) {
  const emitWarning = useTelemetrySender<LabelsTelemetryEvent>({
    type: 'label_opened',
    category: 'warning',
    source: source ?? testId,
    content_type: content_Type ?? undefined,
  }, 'empty', useContextWithOverride(ReactTelemetryContext, telemetryContext));

  const emitCertification = useTelemetrySender<LabelsTelemetryEvent>({
    type: 'label_opened',
    category: 'certification',
    source: source ?? testId,
    content_type: content_Type ?? undefined,
  }, 'empty', useContextWithOverride(ReactTelemetryContext, telemetryContext));

  const emitSensitivity = useTelemetrySender<LabelsTelemetryEvent>({
    type: 'label_opened',
    category: 'sensitivity',
    source: source ?? testId,
    content_type: content_Type ?? undefined,
  }, 'empty', useContextWithOverride(ReactTelemetryContext, telemetryContext));

  return [emitWarning, emitCertification, emitSensitivity];
}

export function useRenderLabels(params: UseRenderLabelsParams): UseRenderLabelsResult {
  const [invoke, data] = useLabels(params);
  const [emitWarningTelemetryEvent, emitCertificationTelemetryEvent, emitSensitivityTelemetryEvent] = useBuildTelemetryEvents(params.content.contentType, params.testId, params.source, params.contextOverrides?.telemetryContext);
  const debouncedData = useLockInLoadedValue(data);

  const warnings = buildWarnings(debouncedData, params, invoke, emitWarningTelemetryEvent);
  const certifications = buildCertifications(debouncedData, params, invoke, emitCertificationTelemetryEvent);
  const sensitivities = buildSensitivities(debouncedData, params, invoke, emitSensitivityTelemetryEvent);

  return { warnings, certifications, sensitivities };
}
